import { useState } from "react";
import {
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import { QuestionMark } from "@mui/icons-material";

import { useAppContext } from "../context/appContext";

function KsbActionButton({
  onClick,
  href,
  icon,
  desktopIcon,
  label = <QuestionMark />,
  variant = "contained",
  size = "medium",
  color = "inherit",
  backgroundColor = undefined, // ✅ NEW
  disabled = false,
  fullWidth = false,
  loading = false,
  sx = {},

  // ✅ confirmation config
  confirm = null,
}) {
  const theme = useTheme();
  const { isMobile } = useAppContext();

  const [openConfirm, setOpenConfirm] = useState(false);

  const isDisabled = disabled || loading;

  /**
   * =========================================================
   * ✅ CLICK HANDLING
   * =========================================================
   */
  const handleClick = (e) => {
    if (isDisabled) return;

    if (confirm) {
      setOpenConfirm(true);
    } else {
      onClick?.(e);
    }
  };

  const handleConfirm = (e) => {
    document.activeElement?.blur();

    setOpenConfirm(false);

    requestAnimationFrame(() => {
      onClick?.(e);
    });
  };

  const handleCancel = () => {
    document.activeElement?.blur();

    setOpenConfirm(false);
  };

  /**
   * =========================================================
   * ✅ BACKGROUND STYLE
   * =========================================================
   */
  const bgStyles = backgroundColor
    ? {
        backgroundColor,
        "&:hover": {
          backgroundColor,
          filter: "brightness(0.9)",
        },
      }
    : {};

  /**
   * =========================================================
   * ✅ RENDER
   * =========================================================
   */
  return (
    <>
      {/* ✅ MOBILE */}
      {isMobile ? (
        <IconButton
          onClick={handleClick}
          disabled={isDisabled}
          size={size}
          sx={{
            color,
            opacity: isDisabled ? 0.6 : 1,
            pointerEvents: isDisabled ? "none" : "auto",
            ...bgStyles,
            ...sx,
          }}
        >
          {loading ? (
            <CircularProgress
              size="1.5rem"
              sx={{
                color: theme.palette.icon?.primary || "inherit",
              }}
            />
          ) : (
            icon
          )}
        </IconButton>
      ) : (
        /* ✅ DESKTOP */
        <Button
          variant={variant}
          onClick={handleClick}
          {...(href ? { href } : {})}
          startIcon={!loading ? desktopIcon : null}
          size={size}
          disabled={isDisabled}
          fullWidth={fullWidth}
          sx={{
            color,
            borderRadius: "45px",
            opacity: isDisabled ? 0.7 : 1,
            ...bgStyles,
            ...sx,
          }}
        >
          {loading ? (
            <CircularProgress
              size="1.5rem"
              sx={{
                color: theme.palette.icon?.primary || "inherit",
              }}
            />
          ) : (
            label
          )}
        </Button>
      )}

      {/* ✅ CONFIRMATION DIALOG */}
      <Dialog open={openConfirm} onClose={handleCancel}>
        <DialogTitle>{confirm?.title || "Are you sure?"}</DialogTitle>

        <DialogContent>
          {confirm?.message || "This action cannot be undone."}
        </DialogContent>

        <DialogActions>
          <MuiButton onClick={handleCancel}>
            {confirm?.cancelLabel || "Cancel"}
          </MuiButton>

          <MuiButton color="error" onClick={handleConfirm}>
            {confirm?.confirmLabel || "Confirm"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default KsbActionButton;
